import { clsx } from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { fill } from '@/copy';
import { syncCopy } from '@/copy/sync';
import { flushOutbox, type OutboxStatus, outbox } from '@/lib/outbox';
import { useOutboxStatus } from '@/lib/useOutbox';
import { Sheet } from '@/ui';
import { Button, Icon } from './kit';
import type { IconName } from './module';

/**
 * The tiny sync indicator in the shell header ("3 plays waiting to sync"). It is empty when
 * everything is sent, shows what is happening while plays wait, a mint "All synced" for a
 * moment when the outbox drains, and turns peach when something needs a person: parked plays,
 * a phone that cannot save, a signed-out or outdated app. Tap it for the sheet with the why.
 * Games and the combine can drop the same chip next to their own pad.
 */

export type ChipTone = 'butter' | 'mint' | 'peach' | 'white';

export interface ChipView {
  tone: ChipTone;
  icon: IconName;
  label: string;
  /** Marks states a person should look at, so the sheet opens with the actions up front. */
  attention: boolean;
}

/** What the chip says for a status. Pure, so a test can pin every state. `justSynced` is the mint flash. */
export function chipView(s: OutboxStatus, justSynced: boolean): ChipView | null {
  const waiting = s.waiting + s.creates;
  const n = waiting;
  if (s.degraded) return { tone: 'peach', icon: 'whistle', label: syncCopy.degraded, attention: true };
  if (s.blocked && waiting > 0) {
    const label =
      s.blocked === 'signed_out'
        ? fill(syncCopy.signedOut, { n })
        : s.blocked === 'update'
          ? fill(syncCopy.update, { n })
          : syncCopy.install;
    return { tone: 'peach', icon: 'whistle', label, attention: true };
  }
  if (s.rejected > 0) {
    const label = s.rejected === 1 ? syncCopy.rejectedOne : fill(syncCopy.rejected, { n: s.rejected });
    return { tone: 'peach', icon: 'whistle', label, attention: true };
  }
  if (waiting > 0) {
    if (s.flushing && !s.offline)
      return { tone: 'butter', icon: 'bolt', label: fill(syncCopy.syncing, { n }), attention: false };
    const label =
      s.waiting === 0
        ? syncCopy.creates
        : s.waiting === 1
          ? syncCopy.waitingOne
          : fill(syncCopy.waiting, { n: s.waiting });
    return { tone: 'butter', icon: 'bolt', label, attention: false };
  }
  if (justSynced) return { tone: 'mint', icon: 'check', label: syncCopy.synced, attention: false };
  return null;
}

const TONE_BG: Record<ChipTone, string> = {
  butter: 'bg-butter',
  mint: 'bg-mint',
  peach: 'bg-peach',
  white: 'bg-white',
};

/** True for a short while after the queue went from something to nothing. */
export function useJustSynced(status: OutboxStatus, ms = 2500): boolean {
  const total = status.waiting + status.creates;
  const [prev, setPrev] = useState(total);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (prev > 0 && total === 0 && !status.blocked && status.rejected === 0 && !status.degraded) {
      setFlash(true);
      const t = window.setTimeout(() => setFlash(false), ms);
      setPrev(total);
      return () => window.clearTimeout(t);
    }
    if (prev !== total) setPrev(total);
  }, [total, prev, status.blocked, status.rejected, status.degraded, ms]);
  return flash;
}

export function SyncChip({ className }: { className?: string }) {
  const status = useOutboxStatus();
  const justSynced = useJustSynced(status);
  const [open, setOpen] = useState(false);
  const view = chipView(status, justSynced);
  if (!view && !open) return null;
  return (
    <>
      {view && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`${syncCopy.sheet.open}: ${view.label}`}
          data-testid="sync-chip"
          data-tone={view.tone}
          className={clsx(
            'relative inline-flex max-w-full items-center gap-1.5 rounded-chip border-3 border-ink px-2.5 py-1 text-xs leading-tight font-bold',
            'shadow-[2px_2px_0_0_var(--color-ink)] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none',
            // Grow the tap area to 44 px without growing the chip.
            'before:absolute before:-inset-x-1 before:-inset-y-2 before:content-[""]',
            TONE_BG[view.tone],
            className,
          )}
        >
          <Icon name={view.icon} size={14} strokeWidth={3} />
          <span className="truncate">{view.label}</span>
        </button>
      )}
      <SyncSheet open={open} onClose={() => setOpen(false)} status={status} />
    </>
  );
}

function reasonFor(code: string | undefined): string {
  const why = syncCopy.why as Record<string, string>;
  return (code && why[code]) || syncCopy.why.other;
}

export function SyncSheet({ open, onClose, status }: { open: boolean; onClose: () => void; status: OutboxStatus }) {
  const [parked, setParked] = useState<{ id: string; code?: string }[]>([]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-read the parked rows when their count changes
  useEffect(() => {
    if (!open) return;
    let alive = true;
    void outbox.rejected().then((rows) => alive && setParked(rows.map((r) => ({ id: r.id, code: r.code }))));
    return () => {
      alive = false;
    };
  }, [open, status.rejected]);

  const groups = useMemo(() => {
    const by = new Map<string, number>();
    for (const r of parked) by.set(r.code ?? 'other', (by.get(r.code ?? 'other') ?? 0) + 1);
    return [...by].map(([code, count]) => ({ code, count }));
  }, [parked]);

  const waiting = status.waiting + status.creates;
  const why = status.degraded
    ? syncCopy.sheet.degraded
    : status.blocked === 'signed_out'
      ? syncCopy.sheet.signedOut
      : status.blocked === 'update'
        ? syncCopy.sheet.update
        : status.blocked === 'install'
          ? syncCopy.sheet.install
          : waiting > 0
            ? status.offline
              ? syncCopy.sheet.offline
              : syncCopy.sheet.onItsWay
            : status.rejected > 0
              ? syncCopy.sheet.turnedDown
              : syncCopy.sheet.allGood;

  return (
    <Sheet open={open} onClose={onClose} title={syncCopy.sheet.title} closeLabel={syncCopy.sheet.close}>
      <div className="flex flex-col gap-4 pb-2">
        <p className="font-medium" data-testid="sync-why">
          {why}
        </p>
        {waiting > 0 && !status.blocked && (
          <Button variant="primary" onClick={() => void flushOutbox()} disabled={status.offline}>
            {syncCopy.sheet.syncNow}
          </Button>
        )}
        {groups.length > 0 && (
          <section className="tts-card-pale flex flex-col gap-2 p-3" aria-label={syncCopy.sheet.rejectedTitle}>
            <h3 className="font-bold">{syncCopy.sheet.rejectedTitle}</h3>
            <ul className="flex flex-col gap-1 text-sm font-medium">
              {groups.map((g) => (
                <li key={g.code}>{fill(syncCopy.sheet.rejectedLine, { n: g.count, why: reasonFor(g.code) })}</li>
              ))}
            </ul>
            <div className="flex gap-3">
              <Button onClick={() => void outbox.requeue(parked.map((r) => r.id))}>{syncCopy.sheet.tryAgain}</Button>
              <Button
                variant="ghost"
                onClick={() => {
                  void outbox.dismiss(parked.map((r) => r.id));
                  setParked([]);
                }}
              >
                {syncCopy.sheet.dismiss}
              </Button>
            </div>
          </section>
        )}
      </div>
    </Sheet>
  );
}
